import React from 'react';
import { Post } from '../types';
import { getRelativeTimeString } from '../utils/time';

interface PostCardProps {
  post: Post;
  onClick: (post: Post) => void;
  onPrefetch?: (post: Post) => void;
  priority?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({ post, onClick, onPrefetch, priority = false }) => {
  const getOptimizedImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    if (url.startsWith('/')) {
      return `${url}${url.includes('?') ? '&' : '?'}v=optimized`;
    }
    const directDomains = ['images.unsplash.com', 'plus.unsplash.com', 'firebasestorage.googleapis.com'];
    try {
      const hostname = new URL(url).hostname;
      if (directDomains.includes(hostname)) return url;
    } catch (e) { }
    return `/api/images/proxy?url=${encodeURIComponent(url)}`;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const seed = post.title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const fallbackPrompt = `Modern minimalist editorial illustration: ${post.title}. high quality, no text`;
    const fallbackUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(fallbackPrompt)}?width=800&height=500&seed=${seed}&nologo=true`;
    e.currentTarget.src = `/api/images/proxy?url=${encodeURIComponent(fallbackUrl)}`;
  };

  if (!post) return null;

  return (
    <a
      href={`/post/${post.slug || post.id}`}
      className="group relative ui-shell overflow-hidden transition-all duration-500 cursor-pointer flex flex-col h-full block text-left hover:-translate-y-1 hover:border-cyan-accent/40"
      onMouseEnter={() => onPrefetch && onPrefetch(post)}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onClick(post);
      }}
    >
      <article className="flex flex-col h-full w-full relative z-10">
        <div className="relative h-64 overflow-hidden flex-shrink-0 bg-slate-900 rounded-t-[1.5rem]">
          <img
            src={getOptimizedImageUrl(post.imageUrl)}
            alt={post.imageAlt || post.title || 'post image'}
            onError={handleImageError}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-700 ease-out grayscale-[12%] group-hover:grayscale-0"
            loading={priority ? 'eager' : 'lazy'}
            {...(priority ? { fetchPriority: 'high' } : {})}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090c14] via-transparent to-transparent opacity-85" />

          <div className="absolute top-4 left-4">
            <span className="bg-deep-void/90 backdrop-blur-sm px-3 py-1 text-[10px] font-bold text-starlight-mist uppercase tracking-[0.2em] border border-white/10 rounded-full">
              {post.category || 'General'}
            </span>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-grow gap-3">
          <div className="flex items-center text-cyan-accent text-[10px] space-x-2 font-bold uppercase tracking-widest">
            <span>{getRelativeTimeString(post.date, post.id)}</span>
          </div>

          <h3 className="text-2xl font-bold text-starlight-mist group-hover:text-cyan-accent transition-colors leading-tight font-serif text-balance">
            {post.title || 'Untitled'}
          </h3>

          <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 text-pretty font-light opacity-85 group-hover:opacity-100 transition-opacity">
            {post.excerpt || ''}
          </p>

          <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between gap-4">
            <div className="flex items-center">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider group-hover:text-starlight-mist transition-colors">Read interpretation</span>
              <svg className="w-4 h-4 ml-2 text-cyan-accent transform translate-x-0 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
      </article>
    </a>
  );
};

export default PostCard;
